// Loan record foundations — ADMIN-only management of the "Loan
// Administration fields" catalog (LoanProfileFieldDefinition). Definitions
// are rows, not columns, so adding a field needs no migration; deactivating
// hides it (and its values) from every loan's Profile tab.

import { useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { Button, FormField, Input } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";
import {
  useCreateProfileFieldDefinition,
  useLoanProfileFieldDefinitions,
  useUpdateProfileFieldDefinition,
} from "@/hooks/portfolioAdmin/usePortfolioAdmin";
import type { LoanProfileFieldDefinitionDto } from "@/api/portfolioAdmin/types";

interface ProfileFieldsManageModalProps {
  loanId: string;
  open: boolean;
  onClose: () => void;
}

const FIELD_TYPES = [
  { value: "text", label: "Text" },
  { value: "number", label: "Number" },
  { value: "date", label: "Date" },
  { value: "yesno", label: "Yes / No" },
];

export function ProfileFieldsManageModal({
  loanId,
  open,
  onClose,
}: ProfileFieldsManageModalProps) {
  const toast = useToast();
  const definitions = useLoanProfileFieldDefinitions(true);
  const create = useCreateProfileFieldDefinition(loanId);

  const [key, setKey] = useState("");
  const [label, setLabel] = useState("");
  const [fieldType, setFieldType] = useState("text");

  const onAdd = async () => {
    try {
      await create.mutateAsync({ key: key.trim(), label: label.trim(), fieldType });
      toast.success(`Field "${label.trim()}" added — it appears on every loan.`);
      setKey("");
      setLabel("");
      setFieldType("text");
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissOnBackdropClick={false}
      title="Manage administration fields"
      description="Fields apply to every loan's Profile tab. Deactivating a field hides it (values are kept)."
      size="lg"
      footer={
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      <div className="flex flex-col gap-space-4" data-testid="profile-fields-manage-modal">
        <div className="flex flex-col gap-space-2">
          {(definitions.data ?? []).map((d) => (
            <DefinitionRow key={d.id} loanId={loanId} definition={d} />
          ))}
          {(definitions.data ?? []).length === 0 && (
            <p className="text-small text-gray-mid italic m-0">
              No administration fields defined yet.
            </p>
          )}
        </div>

        <div className="border-t border-border-light pt-space-3">
          <h4 className="font-display font-semibold text-small text-onyx m-0 mb-space-2">
            Add a field
          </h4>
          <div className="flex flex-col gap-space-2">
            <FormField label="Label" required>
              <Input
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                maxLength={200}
                data-testid="profile-field-label"
              />
            </FormField>
            <FormField
              label="Key"
              required
              helperText="Stable identifier — letters, digits, '-' or '_'."
            >
              <Input
                value={key}
                onChange={(e) => setKey(e.target.value)}
                maxLength={100}
                data-testid="profile-field-key"
              />
            </FormField>
            <FormField label="Type" required>
              <select
                className="px-space-2 py-space-1 border border-border-light rounded-md text-small bg-white-pure"
                value={fieldType}
                onChange={(e) => setFieldType(e.target.value)}
              >
                {FIELD_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </FormField>
            <div>
              <Button
                variant="primary"
                size="sm"
                onClick={() => void onAdd()}
                disabled={!key.trim() || !label.trim() || create.isPending}
                data-testid="profile-field-add"
              >
                {create.isPending ? "Adding…" : "Add field"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}

function DefinitionRow({
  loanId,
  definition,
}: {
  loanId: string;
  definition: LoanProfileFieldDefinitionDto;
}) {
  const toast = useToast();
  const update = useUpdateProfileFieldDefinition(loanId);
  const [label, setLabel] = useState(definition.label);
  const dirty = label.trim() !== definition.label && label.trim().length > 0;

  const save = async (body: { label?: string; isActive?: boolean }) => {
    try {
      await update.mutateAsync({ id: definition.id, body });
      toast.success(`Field "${definition.key}" updated.`);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  return (
    <div
      className="flex items-center gap-space-2 border border-border-light rounded-md px-space-2 py-space-1"
      data-testid={`profile-field-def-${definition.key}`}
    >
      <Input
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        aria-label={`Label for ${definition.key}`}
        className="flex-1"
      />
      <span className="text-caption text-gray-mid shrink-0">
        {definition.key} · {definition.fieldType}
      </span>
      {dirty && (
        <Button
          variant="primary"
          size="sm"
          onClick={() => void save({ label: label.trim() })}
          disabled={update.isPending}
        >
          Save
        </Button>
      )}
      <Button
        variant="tertiary"
        size="sm"
        onClick={() => void save({ isActive: !definition.isActive })}
        disabled={update.isPending}
      >
        {definition.isActive ? "Deactivate" : "Reactivate"}
      </Button>
    </div>
  );
}
